import { type MouseEvent, type ReactNode, useState } from 'react';

import styled from '@emotion/styled';
import { ButtonGhost, Typography } from '@kc-console/common-ui';
import {
  useHandleScrollToEntry,
  useRatioIntersectingEntry,
  useScrollObserverRootContext,
} from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import { IcChevronDownSmallRegular, IcChevronUpSmallRegular } from '@kep/kcds-icons';

type FormSummaryContentsType = {
  label: string;
  summary: ReactNode;
  subInformation?: ReactNode;
};

type FormSummaryItemType = {
  contents: FormSummaryContentsType[];
  formId: string;
  title: string;
};

type FormSummaryType = FormSummaryItemType[];

interface FormViewSummaryProps {
  summaries: FormSummaryType;
  hideSideBorder?: boolean;
  type?: 'default' | 'expandable';
}

const FormViewSummary = ({
  summaries,
  hideSideBorder = false,
  type = 'default',
}: FormViewSummaryProps) => {
  const { t } = useTranslation();
  const { rootId } = useScrollObserverRootContext();
  const entryId = useRatioIntersectingEntry({ rootId });
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const isExpandable = type === 'expandable';

  const handleScrollToEntry = useHandleScrollToEntry({
    entryIds: summaries.map(section => section.formId),
    rootId,
  });

  const handleToggleCollapse = (e: MouseEvent, formId: string) => {
    e.stopPropagation();
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(formId)) {
        newSet.delete(formId);
      } else {
        newSet.add(formId);
      }
      return newSet;
    });
  };

  return (
    <FormSummaryContainer>
      {summaries.map(({ title, formId, contents }) => {
        const isCollapsed = isExpandable && collapsedSections.has(formId);
        const showContents = !isExpandable || !isCollapsed;

        return (
          <FormSummarySection
            key={formId}
            onClick={() => handleScrollToEntry(formId)}
            active={entryId === formId}
            hideSideBorder={hideSideBorder}
          >
            <Typography variant="label/semibold/md" color="text/weak/default">
              {title}
            </Typography>
            {showContents && (
              <FormSummarySectionUl>
                {contents.map(({ label, summary, subInformation }) => (
                  <FormSummarySectionLi key={label}>
                    <Typography variant="body/normal/xs" color="text/weakest/default">
                      {label}
                    </Typography>
                    <FormSummaryContent>
                      <Typography
                        component="span"
                        variant="body/normal/sm"
                        color="text/weak/default"
                      >
                        {summary}
                      </Typography>
                      {subInformation && (
                        <SubInformationTypography
                          component="span"
                          variant="body/normal/sm"
                          color="text/weaker/default"
                        >
                          {subInformation}
                        </SubInformationTypography>
                      )}
                    </FormSummaryContent>
                  </FormSummarySectionLi>
                ))}
              </FormSummarySectionUl>
            )}
            {isExpandable && (
              <CollapseButtonWrapper>
                <CollapseButton
                  variant="secondary"
                  size="sm"
                  onClick={e => handleToggleCollapse(e, formId)}
                  endIcon={
                    isCollapsed ? <IcChevronDownSmallRegular /> : <IcChevronUpSmallRegular />
                  }
                >
                  {isCollapsed ? t('ui:action.detail') : t('ui:action.collapse')}
                </CollapseButton>
              </CollapseButtonWrapper>
            )}
          </FormSummarySection>
        );
      })}
    </FormSummaryContainer>
  );
};

export default FormViewSummary;

// Form Summary 스타일
const FormSummaryContainer = styled.ul`
  display: flex;
  flex-direction: column;
`;

const FormSummarySection = styled.li<{ active: boolean; hideSideBorder?: boolean }>`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  border-left: ${({ theme, active, hideSideBorder }) =>
    hideSideBorder
      ? 'none'
      : active
        ? `2px solid ${theme.color['border/strongest/default']}`
        : `1px solid ${theme.color['border/default']}`};
  padding: ${({ hideSideBorder }) => (hideSideBorder ? '0px 8px 8px 4px' : '0px 16px 24px')};
  gap: 6px;
`;

const FormSummarySectionUl = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormSummarySectionLi = styled.li`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CollapseButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const CollapseButton = styled(ButtonGhost)`
  padding: 4px;
  margin-left: -6px;
  min-width: auto;
`;

const FormSummaryContent = styled.div`
  word-break: break-all;
`;

const SubInformationTypography = styled(Typography)`
  margin-left: 8px;
`;
